// Découpage d'une catégorie de vocabulaire en parties du sentier.
// Partagé par le sentier (CategorySelection) et la navigation « Suivant »
// (trailSequence, App) : tous doivent produire exactement les mêmes plages.

export const TRAIL_SPLIT_SIZE = 20;

/**
 * Plages [start, end) équilibrées d'au plus `max` mots :
 * 23 mots → 12 + 11 (et non 20 + 3), 42 → 14 + 14 + 14.
 * Une seule plage si `total` ≤ `max`.
 */
export const splitIntoParts = (total: number, max = TRAIL_SPLIT_SIZE): Array<{ start: number; end: number }> => {
  if (total <= 0) return [];
  const count = Math.ceil(total / max);
  const base = Math.floor(total / count);
  const extra = total % count; // les `extra` premières parties ont un mot de plus
  const parts: Array<{ start: number; end: number }> = [];
  let start = 0;
  for (let i = 0; i < count; i++) {
    const size = base + (i < extra ? 1 : 0);
    parts.push({ start, end: start + size });
    start += size;
  }
  return parts;
};

/**
 * Place les parties de grammaire d'une unité entre ses catégories de
 * vocabulaire, réparties régulièrement. Renvoie, pour chaque partie,
 * l'indice de la catégorie après laquelle elle se place (la dernière
 * partie tombe toujours après la dernière catégorie).
 * Ex. 4 catégories, 2 parties → après la 2e et la 4e catégorie.
 * Sans catégorie de vocabulaire dans l'unité → -1 (à la fin de l'unité).
 */
export const grammarSlots = (categoryCount: number, grammarCount: number): number[] =>
  Array.from({ length: grammarCount }, (_, j) =>
    categoryCount === 0 ? -1 : Math.ceil(((j + 1) * categoryCount) / grammarCount) - 1
  );

// ── Report de la fin des grosses catégories ──────────────────────────────────
// Au-delà de `keep` mots, la fin de la catégorie (mots les moins courants,
// voir l'ordre des fichiers de données) est placée plus loin sur le sentier,
// après `afterCategory` dans l'unité `unitId`. Même clé de catégorie : la
// progression enregistrée est conservée. Kama a son propre parcours : exclu.
export const DEFERRED_TAILS: Record<string, { keep: number; unitId: number; afterCategory: string }> = {
  'Verbes':     { keep: 40, unitId: 3, afterCategory: 'Temps' },
  'Nourriture': { keep: 40, unitId: 3, afterCategory: 'Objets du quotidien' },
};
// Règles propres à une langue (prioritaires sur DEFERRED_TAILS).
// Yoruba : nombres.ts commence par l'essentiel (0-20, dizaines, 100, 200,
// 1000 = 31 mots) ; les nombres composés (21-99) et les grands nombres
// viennent en fin d'unité 3.
export const DEFERRED_TAILS_BY_LANGUAGE: Record<string, Record<string, { keep: number; unitId: number; afterCategory: string }>> = {
  yo: {
    'Nombres': { keep: 31, unitId: 3, afterCategory: 'Objets du quotidien' },
  },
};
const MIN_DEFERRED_WORDS = 10; // en dessous, reporter ne vaut pas une étape de plus
const NO_DEFERRAL_LANGUAGES = ['ka'];

// ── Découpage par chapitres ──────────────────────────────────────────────────
// Leçons dont les étapes du sentier suivent leurs propres chapitres plutôt
// que des parts égales : chaque clé listée (un titre de section du fichier
// de données) ouvre une nouvelle étape. « Lire les hiéroglyphes » coupait
// sinon au 14e signe, en plein milieu d'une famille de sons.
export const LESSON_BREAKS: Record<string, string[]> = {
  'Lire les hiéroglyphes': [
    'La langue contre le palais', // 2 · n, r, les quatre h, les sifflantes
    'Le fond de la gorge',        // 3 · q, k, g, les dentales
    "Au-delà de l'alphabet",      // 4 · pr, nfr, le signe directeur, le déchiffrement
  ],
  // Yoruba : lettres A-L, lettres M-Y, puis les trois tons
  'Alphabet yoruba': ['M', 'á · ton haut'],
};

/** Clés d'une catégorie nécessaires au découpage par chapitres. */
export interface CategoryKeys {
  /** Toutes les fiches, dans l'ordre du fichier (sans les clés techniques à « _ »). */
  raw: string[];
  /** Les fiches comptées comme mots, dans le même ordre. */
  vocab: string[];
}

/** Plages [start, end) en indices de mots, coupées avant chaque clé de `breakKeys`. */
export const partsFromBreaks = ({ raw, vocab }: CategoryKeys, breakKeys: string[]): Array<{ start: number; end: number }> => {
  const rawIndex = new Map(raw.map((k, i) => [k, i]));
  const cuts = breakKeys
    .map(b => rawIndex.get(b))
    .filter((i): i is number => i !== undefined)
    .map(at => vocab.filter(k => (rawIndex.get(k) ?? -1) < at).length)
    .filter(n => n > 0 && n < vocab.length);
  const bounds = [0, ...Array.from(new Set(cuts)).sort((a, b) => a - b), vocab.length];
  return bounds.slice(1).map((end, i) => ({ start: bounds[i], end }));
};

export interface VocabSplit {
  head: Array<{ start: number; end: number }>;
  tail: Array<{ start: number; end: number }>;
  /** Où placer la fin : unité et catégorie présente après laquelle l'insérer. */
  tailAnchor: { unitId: number; afterCategory: string } | null;
}

/**
 * Découpage d'une catégorie sur le sentier : parties à sa place (`head`) et
 * parties reportées plus loin (`tail`).
 * `unitCategoriesPresent(unitId)` : catégories de cette unité présentes dans la langue.
 */
export const vocabSplit = (
  languageCode: string,
  category: string,
  total: number,
  unitCategoriesPresent: (unitId: number) => string[],
  /** Requis pour les catégories de LESSON_BREAKS, ignoré sinon. */
  keys?: CategoryKeys
): VocabSplit => {
  const breaks = LESSON_BREAKS[category];
  if (breaks && keys) return { head: partsFromBreaks(keys, breaks), tail: [], tailAnchor: null };
  const all: VocabSplit = { head: splitIntoParts(total), tail: [], tailAnchor: null };
  const rule = DEFERRED_TAILS_BY_LANGUAGE[languageCode]?.[category] ?? DEFERRED_TAILS[category];
  if (!rule || NO_DEFERRAL_LANGUAGES.includes(languageCode)) return all;
  if (total - rule.keep < MIN_DEFERRED_WORDS) return all;
  const present = unitCategoriesPresent(rule.unitId).filter(c => c !== category);
  if (present.length === 0) return all;
  const afterCategory = present.includes(rule.afterCategory) ? rule.afterCategory : present[present.length - 1];
  return {
    head: splitIntoParts(rule.keep),
    tail: splitIntoParts(total - rule.keep).map(r => ({ start: r.start + rule.keep, end: r.end + rule.keep })),
    tailAnchor: { unitId: rule.unitId, afterCategory },
  };
};
